import * as THREE from 'three';
import { ROSELoader } from './rose';
import { Color4 } from '../types/color4';

export class RangeFloat {
  min: number;
  max: number;

  constructor(min?: number, max?: number) {
    this.min = min || 0;
    this.max = max || 0;
  }

  getValueInRange(): number {
    return Math.random() * (this.max - this.min) + this.min;
  }
}

export class RangeVector2 {
  min: any;
  max: any;

  constructor(min?: any, max?: any) {
    this.min = min || new THREE.Vector2(0, 0);
    this.max = max || new THREE.Vector2(0, 0);
  }

  getValueInRange(): any {
    let x = Math.random() * (this.max.x - this.min.x) + this.min.x;
    let y = Math.random() * (this.max.y - this.min.y) + this.min.y;
    return new THREE.Vector2(x, y);
  }
}

export class RangeVector3 {
  min: any;
  max: any;

  constructor(min?: any, max?: any) {
    this.min = min || new THREE.Vector3(0, 0, 0);
    this.max = max || new THREE.Vector3(0, 0, 0);
  }

  getValueInRange(): any {
    const x = Math.random() * (this.max.x - this.min.x) + this.min.x;
    const y = Math.random() * (this.max.y - this.min.y) + this.min.y;
    const z = Math.random() * (this.max.z - this.min.z) + this.min.z;
    return new THREE.Vector3(x, y, z);
  }
}

export class RangeColor4 {
  min: any;
  max: any;

  constructor(min?: any, max?: any) {
    this.min = min || new Color4(0, 0, 0, 0);
    this.max = max || new Color4(0, 0, 0, 0);
  }

  getValueInRange(): any {
    const r = Math.random() * (this.max.r - this.min.r) + this.min.r;
    const g = Math.random() * (this.max.g - this.min.g) + this.min.g;
    const b = Math.random() * (this.max.b - this.min.b) + this.min.b;
    const a = Math.random() * (this.max.a - this.min.a) + this.min.a;
    return new Color4(r, g, b, a);
  }
}

/**
 * @enum {Number}
 * @readonly
 */
export const EVENT_TYPE = {
  NONE:       0,
  SIZE:       1,
  TIMER:      2,
  RED:        3,
  GREEN:      4,
  BLUE:       5,
  ALPHA:      6,
  COLOR:     7,
  VELOCITY_X: 8,
  VELOCITY_Y: 9,
  VELOCITY_Z: 10,
  VELOCITY:   11,
  TEXTURE:    12,
  ROTATION:   13
};

/**
 * @enum {Number}
 * @readonly
 */
export const DRAW_TYPE = {
  POINT_SPRITE: 0,
  BILLBOARD: 1
};

/**
 * @enum {Number}
 * @readonly
 */
export const COORD_TYPE = {
  WORLD: 0,
  LOCAL_WORLD: 1,
  LOCAL: 2
};

/**
 * @enum {Number}
 * @readonly
 */
export const ALIGN_TYPE = {
  BILLBOARD: 0,
  WORLD_MESH: 1,
  AXIS_ALIGNED: 2
};

export class ParticleEvent {
  type: number;
  time: RangeFloat = new RangeFloat(0, 0);
  blended: boolean = false;
  size?: RangeVector2;
  timer?: RangeFloat;
  red?: RangeFloat;
  green?: RangeFloat;
  blue?: RangeFloat;
  alpha?: RangeFloat;
  color?: RangeColor4;
  velocityX?: RangeFloat;
  velocityY?: RangeFloat;
  velocityZ?: RangeFloat;
  velocity?: RangeVector3;
  textureIndex?: RangeFloat;
  rotation?: RangeFloat;

  constructor(type: number) {
    this.type = type;
  }
}

export class ParticleEmitter {
  name: string = '';
  events: ParticleEvent[] = [];
  lifeTime: RangeFloat = new RangeFloat(0, 0);
  emitRate: RangeFloat = new RangeFloat(0, 0);
  loopCount: number = 0;
  spawnDirection: RangeVector3 = new RangeVector3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0));
  emitRadius: RangeVector3 = new RangeVector3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0));
  gravity: RangeVector3 = new RangeVector3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0));
  texturePath: string = '';
  particleCount: number = 0;
  alignType: number = 0;
  coordType: number = 0;
  spriteCols: number = 0;
  spriteRows: number = 0;
  drawType: number = 0;
  blendDst: number = 0;
  blendSrc: number = 0;
  blendOp: number = 0;
}

/**
 * @property {ParticleEmitter[]} emitters
 */
export class ParticleSystemData {
  // Static references to enums for backwards compatibility
  static EVENT_TYPE = EVENT_TYPE;
  static DRAW_TYPE = DRAW_TYPE;
  static COORD_TYPE = COORD_TYPE;
  static ALIGN_TYPE = ALIGN_TYPE;
  // Static references to nested classes for backwards compatibility
  static Emitter = ParticleEmitter;
  static Event = ParticleEvent;

  emitters: ParticleEmitter[] = [];

  static loadSizeEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readVector2().multiplyScalar(ZZ_SCALE_IN);
    let max = rh.readVector2().multiplyScalar(ZZ_SCALE_IN);
    evt.size = new RangeVector2(min, max);
    return evt;
  }

  static loadTimerEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat();
    let max = rh.readFloat();
    evt.timer = new RangeFloat(min, max);
    return evt;
  }

  static loadRedEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat();
    let max = rh.readFloat();
    evt.red = new RangeFloat(min, max);
    return evt;
  }

  static loadGreenEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat();
    let max = rh.readFloat();
    evt.green = new RangeFloat(min, max);
    return evt;
  }

  static loadBlueEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat();
    let max = rh.readFloat();
    evt.blue = new RangeFloat(min, max);
    return evt;
  }

  static loadAlphaEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat();
    let max = rh.readFloat();
    evt.alpha = new RangeFloat(min, max);
    return evt;
  }

  static loadColorEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readColor4();
    let max = rh.readColor4();
    evt.color = new RangeColor4(min, max);
    return evt;
  }

  static loadVelocityXEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat() * ZZ_SCALE_IN;
    let max = rh.readFloat() * ZZ_SCALE_IN;
    evt.velocityX = new RangeFloat(min, max);
    return evt;
  }

  static loadVelocityYEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat() * ZZ_SCALE_IN;
    let max = rh.readFloat() * ZZ_SCALE_IN;
    evt.velocityY = new RangeFloat(min, max);
    return evt;
  }

  static loadVelocityZEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat() * ZZ_SCALE_IN;
    let max = rh.readFloat() * ZZ_SCALE_IN;
    evt.velocityZ = new RangeFloat(min, max);
    return evt;
  }

  static loadVelocityEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
    let max = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
    evt.velocity = new RangeVector3(min, max);
    return evt;
  }

  static loadTextureEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat();
    let max = rh.readFloat();
    evt.textureIndex = new RangeFloat(min, max);
    return evt;
  }

  static loadRotationEvent(rh: any, evt: ParticleEvent): ParticleEvent {
    let min = rh.readFloat();
    let max = rh.readFloat();
    evt.rotation = new RangeFloat(min, max);
    return evt;
  }

  static loadEvent(rh: any, type: number): ParticleEvent | null {
    const timeMin = rh.readFloat();
    const timeMax = rh.readFloat();
    let blended = !!rh.readUint8();

    let evt = new ParticleEvent(type);
    evt.time = new RangeFloat(timeMin, timeMax);
    evt.blended = blended;

    switch (type) {
    case EVENT_TYPE.SIZE:
      evt = ParticleSystemData.loadSizeEvent(rh, evt);
      break;
    case EVENT_TYPE.TIMER:
      evt = ParticleSystemData.loadTimerEvent(rh, evt);
      break;
    case EVENT_TYPE.RED:
      evt = ParticleSystemData.loadRedEvent(rh, evt);
      break;
    case EVENT_TYPE.GREEN:
      evt = ParticleSystemData.loadGreenEvent(rh, evt);
      break;
    case EVENT_TYPE.BLUE:
      evt = ParticleSystemData.loadBlueEvent(rh, evt);
      break;
    case EVENT_TYPE.ALPHA:
      evt = ParticleSystemData.loadAlphaEvent(rh, evt);
      break;
    case EVENT_TYPE.COLOR:
      evt = ParticleSystemData.loadColorEvent(rh, evt);
      break;
    case EVENT_TYPE.VELOCITY_X:
      evt = ParticleSystemData.loadVelocityXEvent(rh, evt);
      break;
    case EVENT_TYPE.VELOCITY_Y:
      evt = ParticleSystemData.loadVelocityYEvent(rh, evt);
      break;
    case EVENT_TYPE.VELOCITY_Z:
      evt = ParticleSystemData.loadVelocityZEvent(rh, evt);
      break;
    case EVENT_TYPE.VELOCITY:
      evt = ParticleSystemData.loadVelocityEvent(rh, evt);
      break;
    case EVENT_TYPE.TEXTURE:
      evt = ParticleSystemData.loadTextureEvent(rh, evt);
      break;
    case EVENT_TYPE.ROTATION:
      evt = ParticleSystemData.loadRotationEvent(rh, evt);
      break;
    default:
      return null;
    }

    return evt;
  }

  static load(path: any, callback: any) {
    ROSELoader.load(path, function(rh: any) {
      let i, j, events, emitters, evt, type;
      const data = new ParticleSystemData();

      emitters = rh.readUint32();
      for (i = 0; i < emitters; ++i) {
        const emitter = new ParticleEmitter();
        emitter.name                = rh.readUint32Str();
        emitter.lifeTime.min        = rh.readFloat();
        emitter.lifeTime.max        = rh.readFloat();
        emitter.emitRate.min        = rh.readFloat();
        emitter.emitRate.max        = rh.readFloat();
        emitter.loopCount           = rh.readUint32();
        emitter.spawnDirection.min  = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
        emitter.spawnDirection.max  = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
        emitter.emitRadius.min      = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
        emitter.emitRadius.max      = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
        emitter.gravity.min         = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
        emitter.gravity.max         = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
        emitter.texturePath         = rh.readUint32Str();
        emitter.particleCount       = rh.readUint32();
        emitter.alignType           = rh.readUint32();
        emitter.coordType           = rh.readUint32();
        emitter.spriteCols          = rh.readUint32();
        emitter.spriteRows          = rh.readUint32();
        emitter.drawType            = rh.readUint32();
        emitter.blendDst            = rh.readUint32();
        emitter.blendSrc            = rh.readUint32();
        emitter.blendOp             = rh.readUint32();

        events = rh.readUint32();
        for (j = 0; j < events; ++j) {
          type = rh.readUint32();
          evt = ParticleSystemData.loadEvent(rh, type);

          if (evt) {
            emitter.events.push(evt);
          } else {
            throw "Unexpected event type " + type + " in " + path;
          }
        }

        data.emitters.push(emitter);
      }

      callback(data);
    });
  }
}
